// Analytics

// AI Components
import { AIAssistantSidepanel, FloatingChatButton, useAIAssistant } from '@cobliteam/ai';
import { Analytics } from '@cobliteam/analytics';
// Common Types
import { Location, locationName } from '@cobliteam/common-types';
// Developer Tools
import DeveloperTools from '@cobliteam/developer-tools';
import useBlipChat from '@cobliteam/hooks/src/useBlipChat';
// Hooks
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags, { type FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
// Preferences
import Preferences from '@cobliteam/preferences';
// Store
import { logoutRequest } from '@cobliteam/store/src/Login';
// UI Business Components
import AppHeader from '@cobliteam/ui-business/components/AppHeader';
import AppImpersonate from '@cobliteam/ui-business/components/AppImpersonate';
import AppSidebar from '@cobliteam/ui-business/components/AppSidebar';
import { SidebarInset, SidebarProvider } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
// React and Router
import { type ReactNode, Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ChatWidget from '../../components/ChatWidget';
import AiRecommendationFloatingCard from '../../components/FloatingAiRecommendationCard';
// Local Hooks and Utils
import useRedirectAfterLogin from '../../hooks/useRedirectAfterLogin';
import { initializeThirdParty } from '../../libs/configureThirdParty';
import LoadingPage from '../../pages/LoadingPage';

function AISidepanelStateSync({ pathname, hide, setPosition }: { pathname: string; hide: (value: boolean) => void; setPosition: (position: 'default' | 'tab') => void }) {
  const { isAISidepanelOpen } = useAIAssistant();

  useEffect(() => {
    const hasDefaultTab = pathname === Location.MONITORING || pathname === Location.TRACKING;
    const blipContainer = document.getElementById('blip-chat-container');

    if (isAISidepanelOpen) {
      if (blipContainer) blipContainer.style.setProperty('display', 'none', 'important');
      hide(true);
    } else {
      if (blipContainer) blipContainer.style.removeProperty('display');
      hide(false);
      setPosition(hasDefaultTab ? 'tab' : 'default');
    }

    return () => {
      const blipContainer = document.getElementById('blip-chat-container');
      if (blipContainer) blipContainer.style.removeProperty('display');
      hide(false);
    };
  }, [pathname, setPosition, hide, isAISidepanelOpen]);

  return null;
}

function AppLayout({ children }: { children: ReactNode }) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const dispatch = useDispatch();
  const { setPosition, hide } = useChatWidget();
  const { ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum, sidebar, aiChat, aiRecommendations } = useFeatureFlags();
  const history = useHistory();
  const currentLocation = locationName[history.location.pathname as Location];
  const { getSession, useGetUserData } = useUser();
  const { redirectSessionStorage } = useRedirectAfterLogin();
  const session = getSession();
  const { data: userData } = useGetUserData();
  const currentFleet = session?.fleet;
  const fleets = userData?.userFleets;
  const { build: blipChatBuild } = useBlipChat({ onClose: () => hide(false) });

  // Initialize core services
  useEffect(() => {
    Analytics.initialize();
    Preferences.initialize();
  }, []);

  // Handle redirect after login
  useEffect(() => {
    const redirectUrl = redirectSessionStorage();
    if (redirectUrl) {
      history.push(redirectUrl);
    }
  }, [history, redirectSessionStorage]);

  // Initialize third-party services
  useEffect(() => {
    const featureFlags = {
      ddRumSampleRate,
      ddRumReplaySampleRate,
      oneSignal,
      useDdRum,
      sidebar,
      aiChat,
    } as FeatureFlags;
    if (!isDemoMode) {
      blipChatBuild();
    }
    initializeThirdParty({ session, userData, featureFlags });
  }, [session, userData, ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum, sidebar, aiChat, blipChatBuild, isDemoMode]);

  // Track page views
  useEffect(() => {
    if (!session) return;
    Analytics.pageview(currentLocation, { fleetId: session.fleet.id });
  }, [currentLocation, session]);

  return (
    <AppImpersonate>
      <SidebarProvider>
        <AppSidebar fleets={fleets || []} currentFleet={currentFleet} />
        <SidebarInset>
          <AppHeader user={session.user} logout={() => dispatch(logoutRequest())} />
          <div className='flex h-screen-without-header overflow-hidden'>
            <section id='app-body' data-testid='app-body' className='relative flex-1 overflow-y-auto bg-gray-2'>
              <Suspense fallback={<LoadingPage />}>{children}</Suspense>
              <DeveloperTools />
              {!isDemoMode && <AiRecommendationFloatingCard />}
              {!isDemoMode && aiRecommendations && <FloatingChatButton />}
              {!isDemoMode && <ChatWidget />}
            </section>
            {!isDemoMode && <AISidepanelStateSync pathname={history.location.pathname} hide={hide} setPosition={setPosition} />}
            {!isDemoMode && <AIAssistantSidepanel />}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </AppImpersonate>
  );
}

export default AppLayout;